import {
  HttpResponse,
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { throwError, Observable } from 'rxjs';
import { catchError, finalize, share, tap } from 'rxjs/operators';

import {
  resolveListRouteFromCurrentUrl,
  shouldHandleDetailNotFound,
} from './detail-resource-routes.util';
import { API_ENDPOINTS } from '../api.constants';
import { AuthService } from '../services/auth.service';
import { LoadingService } from '../services/loading.service';
import { ResourceNotFoundService } from '../services/resource-not-found.service';
import { StorageService } from '../services/storage.service';
import { SafeAny } from '../types/safe-any';

@Injectable()
export class Interceptor implements HttpInterceptor {
  private requestCount = 0;

  constructor(
    private readonly loadingService: LoadingService,
    private readonly storage: StorageService,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly resourceNotFoundService: ResourceNotFoundService,
    private readonly injector: Injector
  ) {}

  intercept(req: HttpRequest<SafeAny>, next: HttpHandler): Observable<HttpEvent<SafeAny>> {
    const token = this.storage.getToken();
    if (token && !req.headers.has('Authorization')) {
      req = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      });
    }
    const language = this.storage.getLanguage();
    req = req.clone({
      headers: req.headers.set('Content-Type', 'application/json').set('Language-Locale', language),
    });

    const skipLoader = req.headers.has('X-Skip-Loader');
    if (skipLoader) {
      req = req.clone({ headers: req.headers.delete('X-Skip-Loader') });
    }

    if (req.url.includes('/assets/i18n/') || req.url.includes('assets/i18n/')) {
      return next.handle(req);
    }

    if (!skipLoader) {
      this.requestCount++;
      this.loadingService.setLoading(this.requestCount > 0);
    }

    return this.makeRequest(req, next, !skipLoader);
  }

  makeRequest(
    request: HttpRequest<SafeAny>,
    next: HttpHandler,
    trackLoader: boolean
  ): Observable<HttpEvent<SafeAny>> {
    const req: Observable<HttpEvent<SafeAny>> = next.handle(request).pipe(
      share(),
      tap((event: HttpEvent<SafeAny>) => {
        if (event instanceof HttpResponse) {
          if (request.method !== 'GET') {
            if (event.body && event.body.success) {
              const detail = this.resolveMessage(event.body.message);
              this.messageService.add({
                severity: 'success',
                summary: 'Successo',
                detail,
              });
            } else if (event.body && !event.body.success) {
              const detail = this.resolveMessage(event.body.message);
              this.messageService.add({
                severity: 'error',
                summary: 'Attenzione',
                detail,
              });
            }
          }
        }
      }),
      catchError((err: HttpErrorResponse) => {
        this.handleHttpError(err, request);
        return throwError(() => err);
      }),
      finalize(() => {
        if (!trackLoader) return;
        this.requestCount--;
        this.loadingService.setLoading(this.requestCount > 0);
      })
    );

    return req;
  }

  private handleHttpError(err: HttpErrorResponse, request: HttpRequest<SafeAny>): void {
    const authService = this.injector.get(AuthService);

    if (this.isUnauthorized(err)) {
      if (!this.isPublicAuthRequest(request.url)) {
        authService.forceLogout();
        return;
      }

      const detail = err.error?.message ?? 'Credenziali non valide';
      this.messageService.add({ severity: 'warn', summary: 'Attenzione', detail });
      return;
    }

    // Avoid noisy toasts while redirecting to login (parallel aborted requests → status 0)
    if (authService.isLoggingOut()) {
      return;
    }

    if (err.status === 404 && request.method === 'GET') {
      const apiUrl = err.url ?? request.url;
      if (shouldHandleDetailNotFound(apiUrl, request.method, this.router)) {
        const listRoute = resolveListRouteFromCurrentUrl(this.router);
        if (listRoute) {
          this.resourceNotFoundService.handle(listRoute);
        }
      }
      return;
    }

    if (err.status === 422 || err.status === 400) {
      const detail = err.error?.message ?? 'Dati non validi. Controlla i campi inseriti.';
      this.messageService.add({ severity: 'error', summary: 'Attenzione', detail });
      return;
    }

    if (!navigator.onLine) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore di rete',
        detail: 'Verifica la tua connessione internet.',
      });
      return;
    }

    if (err.status === 0) {
      if (!this.storage.getToken()) {
        return;
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Errore di rete',
        detail: 'Verifica la tua connessione internet.',
      });
      return;
    }

    if (err.status >= 500) {
      const detail = err.error?.message ?? 'Si è verificato un errore. Riprova più tardi.';
      this.messageService.add({
        severity: 'error',
        summary: 'Errore del server',
        detail,
      });
    }
  }

  private isUnauthorized(err: HttpErrorResponse): boolean {
    if (err.status === 401) return true;

    const message = String(err.error?.message ?? err.message ?? '').toLowerCase();
    return message.includes('unauthenticated');
  }

  private isPublicAuthRequest(url: string): boolean {
    const publicPaths = [
      API_ENDPOINTS.AUTH.LOGIN,
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
    ];

    try {
      const path = new URL(url, globalThis.location.origin).pathname.replace(/\/+$/, '');
      return publicPaths.some(
        (endpoint) => path.endsWith(`/${endpoint}`) || path.endsWith(endpoint)
      );
    } catch {
      return publicPaths.some((endpoint) => url.endsWith(`/${endpoint}`) || url.endsWith(endpoint));
    }
  }

  private resolveMessage(message?: string): string {
    if (!message) return '';

    const translated = this.translateService.instant(message);
    return translated !== message ? translated : message;
  }
}
